import {
  Credenza,
  CredenzaBody,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaTrigger,
} from '@/components/ui/credenza.tsx';
import { Button } from '@/components/ui/button.tsx';
import { TrashIcon } from 'lucide-react';
import { ExpandedState, Row, RowData, RowSelectionState } from '@tanstack/react-table';
import React, { useState } from 'react';
import { TTreeDevice } from '@/features/devices/schemas/devices.schemas.ts';
import { useTranslation } from 'react-i18next';
import {
  parseRowPath,
  removeDeviceAtPath,
  shiftExpandedKeys,
} from '@/features/devices/components/device-tree-table/device-tree-table.utils.ts';

export const DeviceTableToolbarDeleteButton = ({
  data,
  hasSelectedRow,
  getSelectedTableRow,
  getSelectedDevice,
  setData,
  setExpandedRows,
  setSelectedRows,
}: {
  data: TTreeDevice[];
  hasSelectedRow: boolean;
  getSelectedTableRow: () => Row<RowData> | undefined;
  getSelectedDevice: () => TTreeDevice | null;
  setData: (devices: TTreeDevice[]) => void;
  setExpandedRows: React.Dispatch<React.SetStateAction<ExpandedState>>;
  setSelectedRows: React.Dispatch<React.SetStateAction<RowSelectionState>>;
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { t } = useTranslation();

  const handleDeviceDelete = () => {
    const selectedRow = getSelectedTableRow();
    if (!selectedRow) return;

    const path = parseRowPath(selectedRow.id);

    setData(removeDeviceAtPath(data, path));
    setSelectedRows({});
    setExpandedRows((prev) => {
      if (prev === true) return true;
      const deletedIndex = path[path.length - 1];
      const parentPath = path.slice(0, -1).join('.');
      return shiftExpandedKeys(prev, parentPath, deletedIndex, -1, deletedIndex);
    });
    setIsDialogOpen(false);
  };

  const selectedDevice = getSelectedDevice();
  const hasSubDevices = selectedDevice?.subDevices && selectedDevice.subDevices.length > 0;

  return (
    <Credenza open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <CredenzaTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className="flex items-center gap-2 rounded-none px-2 py-5 font-normal hover:bg-gray-200 sm:px-3"
          disabled={!hasSelectedRow}
        >
          <TrashIcon className="text-muted-foreground size-4" />
          <span>{t('common.buttons.deleteButton')}</span>
        </Button>
      </CredenzaTrigger>
      <CredenzaContent>
        <CredenzaHeader>
          <CredenzaTitle>{t('devices.actions.confirmDelete')}</CredenzaTitle>
        </CredenzaHeader>
        <CredenzaBody>
          <CredenzaDescription className="hidden">
            {t('devices.actions.confirmDelete')}
            {hasSubDevices ? ` ${t('devices.actions.andAllSubDevices')}` : ''}{' '}
            {t('devices.actions.system')}
          </CredenzaDescription>
          {hasSubDevices ? (
            <>
              {t('devices.actions.questionDeletion1')} {selectedDevice?.sn}{' '}
              {t('devices.actions.questionDeletion2')}
            </>
          ) : (
            <>
              {t('devices.actions.questionDeletion1')} {selectedDevice?.sn}?
            </>
          )}
        </CredenzaBody>
        <CredenzaFooter>
          <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)}>
            {t('common.buttons.cancelButton')}
          </Button>
          <Button type="button" variant="destructive" onClick={handleDeviceDelete}>
            {t('common.buttons.deleteButton')}
          </Button>
        </CredenzaFooter>
      </CredenzaContent>
    </Credenza>
  );
};
